/**
 * Phase 122 — first-party, cookie-free site analytics (replaces HeyCatch).
 *
 * One beacon per page view on the PUBLIC surface (marketing site + coach
 * booking pages). Nothing is stored in the browser: no cookie, no
 * localStorage id. The server derives a daily-rotating visitor hash from
 * ip + user-agent and never keeps the raw values.
 *
 * The server is the source of truth for what counts (it re-classifies the
 * path and drops bots / admin / coach / staff sessions), so this module only
 * has to avoid obvious waste: dashboards, duplicate fires, headless runs.
 */
import { API_BASE } from '@/lib/api';

const UNTRACKED_PREFIXES = [
    '/instructor', '/admin', '/parent', '/staff',
    '/login', '/forgot-password', '/reset-password', '/claim-family', '/pay-invoice',
];

export function isTrackedPath(pathname) {
    if (!pathname || typeof pathname !== 'string') return false;
    if (pathname.startsWith('/coach/')) return true;
    return !UNTRACKED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

let _lastPath = null;
let _entrySent = false;

function utmFromSearch(search) {
    const out = {};
    try {
        const q = new URLSearchParams(search || '');
        for (const k of ['source', 'medium', 'campaign', 'term', 'content']) {
            const v = q.get(`utm_${k}`);
            if (v) out[`utm_${k}`] = v.slice(0, 200);
        }
    } catch { /* ignore malformed query */ }
    return out;
}

/**
 * Fire one page-view beacon for `pathname`. Safe to call on every route
 * change — consecutive calls for the same path are ignored.
 */
export function trackPageview(pathname, search) {
    try {
        if (typeof window === 'undefined' || typeof navigator === 'undefined') return;
        if (navigator.webdriver) return;           // automation / prerender
        if (!isTrackedPath(pathname)) { _lastPath = pathname; return; }
        if (pathname === _lastPath) return;        // same page, e.g. query/hash only
        _lastPath = pathname;

        const entry = !_entrySent;
        _entrySent = true;
        let tz = '';
        try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { /* ignore */ }
        const body = {
            path: pathname,
            // document.referrer is the referrer of the DOCUMENT and does not
            // change on pushState — it is only meaningful for the entry view.
            referrer: entry ? (document.referrer || '') : '',
            ...utmFromSearch(search),
            tz,
            lang: navigator.language || '',
            entry,
            touch: (navigator.maxTouchPoints || 0) > 1,
            w: window.innerWidth || 0,
        };
        const url = `${API_BASE}/analytics/pv`;
        const json = JSON.stringify(body);
        // sendBeacon survives page unloads and never blocks rendering. It
        // can't set Content-Type on a string, so we wrap in a typed Blob.
        if (navigator.sendBeacon) {
            const ok = navigator.sendBeacon(url, new Blob([json], { type: 'application/json' }));
            if (ok) return;
        }
        fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: json,
            keepalive: true,
            credentials: 'include',   // lets the server skip admin/coach sessions
        }).catch(() => { /* fire-and-forget */ });
    } catch { /* analytics must never break the page */ }
}

/** Test hook — reset module state (used by unit tests only). */
export function __resetForTests() { _lastPath = null; _entrySent = false; }
